
import React, { createContext, useContext, ReactNode, useMemo, useCallback, useState, useEffect } from "react";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { SubscriptionFeatures } from "@/lib/types";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useSystemSettings } from "@/hooks/useSystemSettings";

interface FeatureFlagsContextType {
  features: SubscriptionFeatures | null;
  hasFeature: (featureName: keyof SubscriptionFeatures) => boolean;
  isLoading: boolean;
  error: Error | null;
}

const defaultFeatures: SubscriptionFeatures = {
  currency_conversion: false,
  multiple_currencies: false,
  price_comparison: false,
  grouped_contracts: false,
  contract_services: false,
  reminders: false,
  payment_type_selection: false,
  dashboard: false,
  contract_pdf_email: false,
  document_extraction: false,
  // Numeric limits - null = unlimited when subscriptions disabled
  max_contracts: null,
  max_users: null,
  max_erp_integrations: 0,
  monthly_doc_extractions: 0,
  monthly_signings: 0,
  storage_gb: 1,
  // Premium feature flags
  erp_integration: false,
  custom_fields: false,
  calendar_view: false,
  contract_notes: false,
  duplicate_detection: false,
  api_read: false,
  api_write: false,
  advanced_dashboard: false,
  data_health: false,
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  features: defaultFeatures,
  hasFeature: () => false,
  isLoading: true,
  error: null
});

export const useFeatureFlags = () => useContext(FeatureFlagsContext);

// Debug mode should be false in production
const DEBUG_FEATURES = false; 

// Helper function to fetch public settings
const fetchPublicSetting = async (key: string, defaultValue: string = ""): Promise<string> => {
  try {
    const { data, error } = await supabase.rpc('fetch_public_setting', { setting_key: key });
    
    if (error) {
      console.error(`Error loading ${key} setting:`, error);
      return defaultValue;
    }
    
    return data || defaultValue;
  } catch (error) {
    console.error(`Error fetching ${key} setting:`, error);
    return defaultValue;
  }
};

export const FeatureFlagsProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const { useSubscriptionFeaturesQuery } = useSubscriptions();
  const { getPublicBooleanSetting } = useSystemSettings();
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState<boolean>(true);
  const [isCheckingSettings, setIsCheckingSettings] = useState<boolean>(true);
  
  // Check if subscriptions are enabled
  useEffect(() => {
    const checkSubscriptionsEnabled = async () => {
      try {
        setIsCheckingSettings(true);
        const enabled = await getPublicBooleanSetting('subscriptions_enabled', true);
        setSubscriptionsEnabled(enabled);
      } catch (error) {
        console.error("Error checking if subscriptions are enabled:", error);
        // Default to true if there's an error
        setSubscriptionsEnabled(true);
      } finally {
        setIsCheckingSettings(false);
      }
    };
    
    checkSubscriptionsEnabled();
  }, [getPublicBooleanSetting]);
  
  const { 
    data: features, 
    isLoading: isLoadingFeatures, 
    error 
  } = useSubscriptionFeaturesQuery({
    enabled: isAuthenticated,
    retry: true,
    retryDelay: 1000,
    staleTime: 1000 * 60 * 5, // Consider data stale after 5 minutes
    cacheTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false // Don't refetch on window focus to reduce API calls
  });

  // Determine if we're still loading anything
  const isLoading = isLoadingFeatures || isCheckingSettings;

  // Log subscription features once when they are initially loaded
  if (DEBUG_FEATURES && features && !isLoading) {
    console.log("Subscription features loaded:", features);
  }

  // Memoize feature checking function to prevent unnecessary re-renders
  const hasFeature = useMemo(() => {
    // Keep track of previous feature accesses to only log changes
    const previousAccess = new Map<string, boolean>();
    
    // List of boolean feature keys (exclude numeric limits)
    const booleanFeatures = [
      'currency_conversion', 'multiple_currencies', 'price_comparison',
      'grouped_contracts', 'contract_services', 'reminders',
      'payment_type_selection', 'dashboard', 'contract_pdf_email',
      'document_extraction', 'erp_integration', 'custom_fields',
      'calendar_view', 'contract_notes', 'duplicate_detection',
      'api_read', 'api_write', 'advanced_dashboard', 'data_health',
      'sales_analytics', 'cost_optimization', 'governing_documents'
    ] as const;
    
    return (featureName: keyof SubscriptionFeatures): boolean => {
      // If we're still loading or not authenticated, deny access
      if (isLoading || !isAuthenticated) {
        return false;
      }
      
      // If subscriptions are disabled, grant access to all features
      if (!subscriptionsEnabled) {
        return true;
      }
      
      // If we have subscription features data, use that
      if (features) {
        const featureValue = features[featureName];
        
        // For numeric limits, return true if the value is null (unlimited) or > 0
        if (!booleanFeatures.includes(featureName as any)) {
          const hasAccess = featureValue === null || (typeof featureValue === 'number' && featureValue > 0);
          return hasAccess;
        }
        
        // For boolean features, return the boolean value
        const hasAccess = Boolean(featureValue);
        
        // Only log if DEBUG_FEATURES is enabled and access has changed
        if (DEBUG_FEATURES && previousAccess.get(featureName) !== hasAccess) {
          console.log(`FeatureFlags - Feature ${featureName} access:`, hasAccess);
          previousAccess.set(featureName, hasAccess);
        }
        
        return hasAccess;
      }
      
      const defaultValue = defaultFeatures[featureName];
      if (typeof defaultValue === 'boolean') {
        return defaultValue;
      }
      // For numeric defaults, return true if null or > 0
      return defaultValue === null || (typeof defaultValue === 'number' && defaultValue > 0);
    };
  }, [features, isLoading, isAuthenticated, subscriptionsEnabled]);

  const contextValue = useMemo(() => ({
    features: features || defaultFeatures,
    hasFeature,
    isLoading,
    error
  }), [features, hasFeature, isLoading, error]);

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
